/**
 * Árvore de navegação do Nexora — fonte única de verdade dos menus.
 *
 * Espelha a arquitetura de informação do protótipo (`base-html/tools/routes.json`:
 * 14 módulos, 81 itens, 128 telas). Duas diferenças deliberadas em relação ao
 * protótipo, e o motivo de cada uma:
 *
 * 1. Só entram como link os itens com ROTA REAL registrada no router. O protótipo
 *    tinha 1160 `href="#"` mortos; repetir isso aqui seria mentir no menu. Telas do
 *    protótipo ainda não implementadas aparecem com `planned: true` — visíveis para
 *    manter a IA legível, mas não clicáveis.
 * 2. 37 das 69 páginas do frontend não têm tela correspondente no protótipo
 *    (IAM, Organização, Integrações, Dados Mestres, Compras, Suporte,
 *    Observabilidade). Elas são funcionalidade real e ficam agrupadas ao final.
 *
 * Cobertura atual frente ao protótipo: 23 telas equivalentes, 28 parciais,
 * 77 inexistentes (mapa em `frontend/.review-loop/reports/`).
 */

export interface NavItem {
  /** Rota real do TanStack Router. Ausente quando `planned`. */
  readonly path?: string;
  readonly label: string;
  /** Tela do protótipo que este item representa (slug de base-html). */
  readonly screen?: string;
  /** Tela prevista no protótipo e ainda não implementada: exibida, não clicável. */
  readonly planned?: boolean;
  /** Filhos exibidos como abas no topo do conteúdo, não no rail. */
  readonly tabs?: readonly NavTab[];
}

export interface NavTab {
  readonly path: string;
  readonly label: string;
  readonly screen?: string;
}

export interface NavModule {
  readonly id: string;
  readonly label: string;
  /** Nome de ícone Material Symbols Outlined. */
  readonly icon: string;
  /** `true` para os módulos que existem no protótipo mas não no frontend. */
  readonly planned?: boolean;
  readonly items: readonly NavItem[];
}

/** Módulos que espelham 1:1 os 14 do protótipo. */
export const PROTOTYPE_MODULES: readonly NavModule[] = [
  {
    id: 'visao-geral',
    label: 'Visão Geral',
    icon: 'space_dashboard',
    items: [
      { path: '/dashboard', label: 'Painel Executivo', screen: 'erpone-dashboard-executivo' },
      { path: '/dashboard/performance', label: 'Performance Global', screen: 'erpone-dashboard-de-performance-global' },
      { path: '/okrs/productivity', label: 'Produtividade e Eficiência', screen: 'erpone-an-lise-de-produtividade-e-efici-ncia' },
      { path: '/okrs', label: 'OKRs e Metas', screen: 'erpone-gest-o-de-okrs-e-resultados-chave' },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: 'groups',
    items: [
      { path: '/crm/pipeline', label: 'Pipeline de Vendas', screen: 'erpone-pipeline-de-vendas-kanban' },
      { path: '/crm/funnel', label: 'Funil de Vendas', screen: 'erpone-funil-de-vendas-analytics' },
      { path: '/crm/opportunities/new', label: 'Nova Oportunidade', screen: 'erpone-nova-oportunidade-de-venda' },
      { path: '/master-data/customers', label: 'Clientes', screen: 'erpone-gest-o-de-clientes-crm' },
      { path: '/master-data/customers/new', label: 'Novo Cliente', screen: 'erpone-cadastro-de-novo-cliente' },
      {
        path: '/crm/customers',
        label: 'Ficha do Cliente',
        screen: 'erpone-detalhes-do-cliente-vis-o-360',
        tabs: [
          { path: '/crm/customers', label: 'Visão 360', screen: 'erpone-detalhes-do-cliente-vis-o-360' },
          { path: '/crm/customers', label: 'Contratos', screen: 'erpone-detalhes-do-cliente-contratos' },
          { path: '/crm/customers', label: 'Interações', screen: 'erpone-detalhes-do-cliente-intera-es' },
          { path: '/crm/customers', label: 'Pedidos e Vendas', screen: 'erpone-detalhes-do-cliente-pedidos-e-vendas' },
        ],
      },
      {
        path: '/master-data/parties',
        label: 'Contatos',
        screen: 'erpone-diret-rio-global-de-contatos',
        tabs: [
          { path: '/master-data/parties', label: 'Diretório Global', screen: 'erpone-diret-rio-global-de-contatos' },
          { path: '/master-data/parties', label: 'Contatos e Interações', screen: 'erpone-gest-o-de-contatos-e-intera-es' },
          { path: '/master-data/parties', label: 'Detalhe da Interação', screen: 'erpone-detalhes-da-intera-o' },
        ],
      },
      {
        path: '/crm/commissions',
        label: 'Comissões',
        screen: 'erpone-dashboard-de-comiss-es-e-incentivos',
        tabs: [
          { path: '/crm/commissions', label: 'Painel', screen: 'erpone-dashboard-de-comiss-es-e-incentivos' },
          { path: '/crm/commissions', label: 'Regras de Comissão', screen: 'erpone-configura-o-de-regras-de-comiss-o' },
          { path: '/crm/commissions', label: 'Pagamento', screen: 'erpone-processamento-de-pagamento-de-comiss-es' },
        ],
      },
    ],
  },
  {
    id: 'vendas-e-pdv',
    label: 'Vendas e PDV',
    icon: 'point_of_sale',
    items: [
      { path: '/sales', label: 'Pedidos de Venda', screen: 'erpone-pedido-de-venda' },
      { path: '/sales/new', label: 'Novo Pedido', screen: 'erpone-novo-pedido-de-venda' },
      { path: '/sales/quotes', label: 'Orçamentos' },
      { path: '/pos/sale', label: 'PDV — Frente de Caixa', screen: 'erpone-pdv-frente-de-caixa' },
      { path: '/pos/session', label: 'Sessão de Caixa', screen: 'erpone-relat-rio-de-fechamento-de-dia' },
      { path: '/pos/terminals', label: 'Terminais' },
      { path: '/sales/checkout', label: 'Checkout — Faturamento', screen: 'erpone-checkout-faturamento' },
    ],
  },
  {
    id: 'compras',
    label: 'Compras',
    icon: 'shopping_bag',
    items: [
      { path: '/procurement/requests', label: 'Requisições' },
      { path: '/procurement/rfqs', label: 'Cotações (RFQ)' },
      { path: '/procurement/orders', label: 'Pedidos de Compra', screen: 'erpone-novo-pedido-de-compra-corrigido' },
      { path: '/procurement/receipts', label: 'Recebimentos' },
    ],
  },
  {
    id: 'contratos',
    label: 'Contratos',
    icon: 'handshake',
    items: [
      { path: '/contracts', label: 'Repositório de Contratos', screen: 'erpone-reposit-rio-global-de-contratos' },
      { path: '/contracts/$id', label: 'Detalhe e Edição' },
      { path: '/contracts/$id/billing-schedule', label: 'Cronograma de Faturamento', screen: 'erpone-cronograma-de-faturamento-de-contrato' },
      { path: '/contracts/renewals', label: 'Renovação Automática', screen: 'erpone-fluxo-de-renova-o-de-contratos' },
      { path: '/approvals', label: 'Central de Aprovações', screen: 'erpone-central-de-aprova-es' },
      { path: '/approval-rules', label: 'Regras de Alçada', screen: 'erpone-configura-o-de-regras-de-al-ada' },
    ],
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    icon: 'account_balance_wallet',
    items: [
      { path: '/finance', label: 'Painel Financeiro', screen: 'erpone-financeiro' },
      { path: '/finance/payables', label: 'Contas a Pagar', screen: 'erpone-gest-o-de-t-tulos-contas-a-pagamento' },
      { path: '/finance/receivables', label: 'Contas a Receber', screen: 'erpone-gest-o-de-t-tulos-contas-a-receber' },
      { path: '/finance/receivables-dashboard', label: 'Contas a Receber (Dashboard)', screen: 'erpone-contas-a-receber-dashboard' },
      { path: '/finance/payment-terms', label: 'Prazos e Parcelamento', screen: 'erpone-configura-o-de-prazos-e-parcelamento' },
      { path: '/finance/payment-methods', label: 'Formas de Pagamento', screen: 'erpone-gest-o-de-formas-de-pagamento' },
    ],
  },
  {
    id: 'tesouraria-e-bancos',
    label: 'Tesouraria e Bancos',
    icon: 'account_balance',
    items: [
      { path: '/finance/accounts', label: 'Contas Bancárias', screen: 'erpone-gest-o-de-contas-banc-rias' },
      { path: '/finance/reconciliation', label: 'Conciliação Bancária', screen: 'erpone-concilia-o-banc-ria' },
      { path: '/finance/payments-cnab', label: 'Pagamentos CNAB/API', screen: 'erpone-processamento-de-pagamentos-cnab-api' },
      { path: '/finance/tariffs', label: 'Tarifas e Taxas Bancárias', screen: 'erpone-configura-o-de-tarifas-e-taxas-banc-rias' },
      { path: '/finance/mdr-rates', label: 'Taxas de Operadoras', screen: 'erpone-taxas-de-operadoras-e-antecipa-o' },
    ],
  },
  {
    id: 'contabilidade',
    label: 'Contabilidade',
    icon: 'menu_book',
    items: [
      { path: '/accounting', label: 'Painel Contábil' },
      { path: '/accounting/chart', label: 'Plano de Contas', screen: 'erpone-plano-de-contas-interativo' },
      { path: '/accounting/journal', label: 'Lançamentos Contábeis', screen: 'erpone-lan-amentos-cont-beis-integrados' },
      {
        path: '/accounting/statements',
        label: 'Demonstrações',
        screen: 'erpone-demonstra-es-financeiras-dre',
      },
      { path: '/accounting/revenue-recognition', label: 'Reconhecimento de Receita', screen: 'erpone-reconhecimento-de-receita-e-diferimento' },
    ],
  },
  {
    id: 'fiscal',
    label: 'Fiscal',
    icon: 'calculate',
    items: [
      { path: '/fiscal/tax-rates', label: 'Configuração de Tributos', screen: 'erpone-configura-o-de-tributos' },
      { path: '/fiscal/tax-rules', label: 'Regras Fiscais', screen: 'erpone-reposit-rio-global-de-regras-fiscais' },
      { path: '/fiscal/tax-profiles', label: 'Perfis Fiscais' },
      { path: '/fiscal/tax-calculate', label: 'Simulador de Impacto', screen: 'erpone-simulador-de-impacto-fiscal-what-if' },
      { path: '/fiscal/tax-reform', label: 'Reforma Tributária', screen: 'erpone-painel-de-transi-o-reforma-tribut-ria' },
      { path: '/fiscal/cfop-table', label: 'Tabela CFOP' },
      { path: '/fiscal/cst-tables', label: 'Tabelas CST' },
      { path: '/fiscal/ncm-table', label: 'Tabela NCM' },
      { path: '/fiscal/dashboard', label: 'Painel de Apuração', screen: 'erpone-painel-de-apura-o-e-conformidade-fiscal' },
      { path: '/fiscal/tax-variables', label: 'Variáveis e Fórmulas', screen: 'erpone-gest-o-de-vari-veis-e-f-rmulas-fiscais' },
      { path: '/fiscal/uf-matrix', label: 'Matriz de Exceções UF×UF', screen: 'erpone-matriz-de-exce-es-fiscais-uf-x-uf' },
    ],
  },
  {
    id: 'documentos-fiscais',
    label: 'Documentos Fiscais',
    icon: 'receipt_long',
    items: [
      { path: '/fiscal/invoices', label: 'Documentos Emitidos', screen: 'erpone-gest-o-de-documentos-fiscais-nf-e' },
      { path: '/fiscal/invoices/new', label: 'Emitir Documento', screen: 'erpone-valida-o-e-emiss-o-fiscal' },
      { path: '/fiscal/documents/nfse-pipeline', label: 'Faturamento NFS-e', screen: 'erpone-faturamento-de-servi-os-nfs-e' },
      { path: '/fiscal/documents/product-mapping', label: 'Importação de XML (De-Para)', screen: 'erpone-importa-o-de-notas-fiscais-xml' },
      { path: '/fiscal/documents/events', label: 'Eventos e Carta de Correção', screen: 'erpone-eventos-fiscais-e-carta-de-corre-o' },
    ],
  },
  {
    id: 'obrigacoes-e-sped',
    label: 'Obrigações e SPED',
    icon: 'assignment_turned_in',

    items: [
      { path: '/obligations', label: 'Painel de Obrigações', screen: 'erpone-painel-de-obriga-es-fiscais' },
      { path: '/obligations/guias', label: 'Guias e Recolhimento', screen: 'erpone-central-de-guias-e-recolhimento' },
      { path: '/obligations/sped', label: 'Assistente SPED', screen: 'erpone-assistente-de-gera-o-sped' },
      { path: '/obligations/transmissions', label: 'Arquivos Transmitidos', screen: 'erpone-reposit-rio-de-arquivos-transmitidos' },
    ],
  },
  {
    id: 'fechamento',
    label: 'Fechamento',
    icon: 'lock_clock',

    items: [
      { path: '/closing', label: 'Fechamento Mensal', screen: 'erpone-painel-de-controle-fechamento-mensal' },
      { path: '/closing/periods/$id/reconciliation', label: 'Fiscal × Contábil', screen: 'erpone-concilia-o-fiscal-vs-cont-bil-fechamento' },
      { path: '/closing/portal', label: 'Portal do Contador', screen: 'erpone-portal-do-contador' },
    ],
  },
  {
    id: 'estoque-e-producao',
    label: 'Estoque e Produção',
    icon: 'factory',
    items: [
      { path: '/inventory/stock', label: 'Gestão de Estoque', screen: 'erpone-gest-o-de-estoque' },
      { path: '/inventory/movements', label: 'Movimentações (Kardex)', screen: 'erpone-movimenta-o-de-estoque-kardex' },
      { path: '/inventory/tracking', label: 'Lotes e Rastreabilidade', screen: 'erpone-controle-de-lotes-e-rastreabilidade' },
      { path: '/inventory/warehouses', label: 'Armazéns' },
      { path: '/inventory/counts', label: 'Inventário Físico' },
      { path: '/inventory/reservations', label: 'Reservas' },
      { path: '/manufacturing/production-orders', label: 'Ordens de Produção', screen: 'erpone-gest-o-de-ordens-de-produ-o-op' },
      { path: '/manufacturing/boms', label: 'Estrutura de Produto (BOM)', screen: 'erpone-estrutura-de-produto-bom' },
      { path: '/manufacturing/shop-floor', label: 'Apontamento Shop Floor', screen: 'erpone-apontamento-de-produ-o-shop-floor' },
      { path: '/manufacturing/service-orders', label: 'Ordens de Serviço', screen: 'erpone-gest-o-de-ordens-de-servi-o-os' },
    ],
  },
  {
    id: 'logistica',
    label: 'Logística',
    icon: 'local_shipping',
    items: [
      { path: '/logistics/ctes', label: 'CT-e', screen: 'erpone-gest-o-de-ct-e-conhecimentos-de-transporte' },
      { path: '/logistics/deliveries', label: 'Monitor de Entregas', screen: 'erpone-monitor-de-entregas-e-ocorr-ncias-ct-e' },
      { path: '/logistics/freight-tables', label: 'Tabela de Fretes', screen: 'erpone-tabela-de-fretes-e-componentes-ct-e' },
      { path: '/logistics/mdfes', label: 'MDF-e / Manifestos', screen: 'erpone-gest-o-de-mdf-e-manifestos' },
    ],
  },
  {
    id: 'okrs',
    label: 'OKRs e Metas',
    icon: 'track_changes',
    items: [
      { path: '/okrs', label: 'OKRs', screen: 'erpone-gest-o-de-okrs-e-resultados-chave' },
      { path: '/okrs/goals', label: 'Metas Departamentais', screen: 'erpone-configura-o-de-metas-e-objetivos' },
      { path: '/okrs/productivity', label: 'Produtividade', screen: 'erpone-an-lise-de-produtividade-e-efici-ncia' },
    ],
  },
];

/**
 * Módulos sem contrapartida no protótipo. São funcionalidade real e não podem
 * sair do menu só porque o protótipo não as desenhou.
 */
export const PLATFORM_MODULES: readonly NavModule[] = [
  {
    id: 'dados-mestres',
    label: 'Dados Mestres',
    icon: 'inventory',
    items: [
      { path: '/master-data/products', label: 'Produtos' },
      { path: '/master-data/services', label: 'Serviços' },
      { path: '/master-data/categories', label: 'Categorias' },
      { path: '/master-data/import', label: 'Importação' },
    ],
  },
  {
    id: 'organizacao',
    label: 'Organização',
    icon: 'corporate_fare',
    items: [
      { path: '/organization', label: 'Visão Geral' },
      { path: '/organization/companies', label: 'Empresas' },
      { path: '/organization/branches', label: 'Filiais' },
      { path: '/organization/number-sequences', label: 'Sequências Numéricas' },
    ],
  },
  {
    id: 'acessos',
    label: 'Acessos',
    icon: 'admin_panel_settings',
    items: [
      { path: '/iam/users', label: 'Usuários' },
      { path: '/iam/roles', label: 'Papéis' },
      { path: '/iam/memberships', label: 'Membros' },
      { path: '/iam/sessions', label: 'Sessões' },
      { path: '/iam/access-requests', label: 'Solicitações de Acesso' },
    ],
  },
  {
    id: 'integracoes',
    label: 'Integrações',
    icon: 'hub',
    items: [
      { path: '/integrations', label: 'Visão Geral' },
      { path: '/integrations/applications', label: 'Aplicações' },
      { path: '/integrations/webhooks', label: 'Webhooks' },
      { path: '/integrations/exports', label: 'Exportações' },
      { path: '/integrations/imports', label: 'Importações' },
    ],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    icon: 'settings',
    items: [
      { path: '/onboarding/setup', label: 'Implantação', screen: 'erpone-checklist-de-implanta-o' },
      { path: '/documents', label: 'Documentos' },
      { path: '/audit', label: 'Auditoria', screen: 'erpone-auditoria-e-governan-a-de-dados-performance' },
      { path: '/support/tickets', label: 'Chamados' },
      { path: '/retention/rules', label: 'Regras de Retenção', screen: 'erpone-configura-o-de-regras-de-reten-o' },
    ],
  },
];

/** Módulos visíveis apenas para papéis administrativos. */
export const ADMIN_MODULES: readonly NavModule[] = [
  {
    id: 'observabilidade',
    label: 'Observabilidade',
    icon: 'monitor_heart',
    items: [{ path: '/admin/logs', label: 'Logs de Erro' }],
  },
];

/** Itens de um módulo que têm rota real. */
export function liveItems(mod: NavModule): readonly NavItem[] {
  return mod.items.filter((i) => typeof i.path === 'string' && !i.planned);
}

/** Achatamento linear para a paleta de busca e para navegação anterior/próxima. */
export interface FlatEntry {
  readonly path: string;
  readonly label: string;
  readonly moduleId: string;
  readonly moduleLabel: string;
  readonly moduleIcon: string;
}

export function flattenModules(modules: readonly NavModule[]): FlatEntry[] {
  const out: FlatEntry[] = [];
  for (const mod of modules) {
    for (const item of liveItems(mod)) {
      out.push({
        path: item.path as string,
        label: item.label,
        moduleId: mod.id,
        moduleLabel: mod.label,
        moduleIcon: mod.icon,
      });
    }
  }
  return out;
}

/** Resolve a rota ativa para breadcrumb: casa o path mais específico. */
export function findByPath(
  modules: readonly NavModule[],
  pathname: string,
): { module: NavModule; item: NavItem } | null {
  let best: { module: NavModule; item: NavItem } | null = null;
  let bestLen = -1;
  for (const mod of modules) {
    for (const item of liveItems(mod)) {
      const p = item.path as string;
      const exact = pathname === p;
      const prefix = pathname.startsWith(p.endsWith('/') ? p : `${p}/`);
      if ((exact || prefix) && p.length > bestLen) {
        best = { module: mod, item };
        bestLen = p.length;
      }
    }
  }
  return best;
}
